import { FC, useEffect, useState } from 'react';
import * as csvParser from 'papaparse';
import { EStatus } from '@/models/fileUpload';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useLazyGetDataSourceTmpLinesQuery } from '@/services/dataSources';
import { updateFileData } from '@/features/fileUploading/fileUploadingSlice';
import { SpinnerIcon } from '@/lib';
import { StepPageActions } from '@/global/components';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { CSVRowIsHeaderOrItem } from '@/features/dataSources/components/commons/CSVRowIsHeaderOrItem';
import { CSVFileDataTable } from '@/features/dataSources/components/commons/CSVFileDataTable';
import { DataSource, DataSourceFileConfig } from '@/models/dataSource';
import { useTranslation } from 'react-i18next';
import { USE_MOCK_CSV_HEADER_STEP } from '../mockCsvFileData';

type FileDataSourceCSVHeaderProps = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
};

const DEFAULT_HEADER = 'defaultHeader';
const ITEM_HEADER = 'itemHeader';

export const FileDataSourceCSVHeaderStep: FC<FileDataSourceCSVHeaderProps> = ({ onNextStep, onPrevStep }) => {
  const dispatch = useAppDispatch();
  const { id: workspaceId } = useWorkspace();
  const { t } = useTranslation('data_source_edit');

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;
  const fileData = useAppSelector((state) => state.fileUploading.fileData);
  const csvFileData = useAppSelector((state) => state.fileUploading.fileData);
  const fileUploadStatus = useAppSelector((state) => state.fileUploading.status);
  const encrypted = useAppSelector((state) => state.fileUploading.encrypted);

  const [csvHeader, setCSVHeader] = useState<string>(csvFileData?.isDefaultHeader ? DEFAULT_HEADER : ITEM_HEADER);
  const [encryptedLoading, setEncryptedLoading] = useState(false);

  const [getLines, { isLoading: isLoadingTmpLines, isSuccess: isSuccessTmpLines, data: tmpLines }] = useLazyGetDataSourceTmpLinesQuery();

  useEffect(() => {
    if (USE_MOCK_CSV_HEADER_STEP) return;

    if (encrypted) {
      if (fileUploadStatus?.uploadingStatus == EStatus.SUCCESS) {
        console.log('start preview download', fileUploadStatus);
        const key = fileUploadStatus?.data?.path;
        if (key) {
          getLines({ workspaceId, key, limit: 10 });
        }
      } else if (fileUploadStatus?.uploadingStatus == EStatus.UPLOADING) {
        setEncryptedLoading(true);
      } else {
        setEncryptedLoading(false);
      }
    }
  }, [encrypted, fileUploadStatus]);

  useEffect(() => {
    if (USE_MOCK_CSV_HEADER_STEP) return;

    if (isSuccessTmpLines && tmpLines) {
      console.log(tmpLines);
      csvParser.parse(tmpLines.slice(0, 10).join('\n'), {
        skipEmptyLines: true,
        complete: (results: csvParser.ParseResult<string[]>) => {
          console.log(results);
          dispatch(updateFileData({ preview: results.data, header: results.data[0], isDefaultHeader: true }));
        },
      });
    }
  }, [isSuccessTmpLines]);

  const handleNextStep = () => {
    if (dataSource && dataSource.configuration && fileData?.isDefaultHeader !== undefined) {
      dispatch(stashDataSource({ configuration: { ...dataSource.configuration, isDefaultHeader: fileData.isDefaultHeader } }));

      onNextStep();
    }
  };

  return (
    <>
      <div className='flex flex-col justify-start gap-8'>
        {(isLoadingTmpLines || encryptedLoading) && (
          <div className='mb-2 flex h-7 justify-center text-center'>
            <SpinnerIcon className='size-8 text-blue-900' />
          </div>
        )}
        <p className='text-sm font-semibold text-gray-700'>{t('file.header.is_first_line_header')}</p>
        <CSVFileDataTable csvFileData={csvFileData} csvHeader={csvHeader} />
      </div>
      <CSVRowIsHeaderOrItem csvHeader={csvHeader} onTypeChange={setCSVHeader} />

      <StepPageActions
        primaryButton={{
          text: 'COMMON.confirm',
          onClick: handleNextStep,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onPrevStep,
        }}
      />
    </>
  );
};
